import React from 'react';
import {
  Box, TextField, FormControlLabel, Switch,
  Chip, Autocomplete, Checkbox,
} from '@mui/material';
import type { HockeySettings, HockeyTieBreaker } from '@/types';
import type { SportSettingsFormProps } from './types';
import { SettingsSection } from './SettingsSection';
import { SettingsField, SettingsFieldsGrid } from './SettingsFieldsGrid';

const TIEBREAKER_OPTIONS: { value: HockeyTieBreaker; label: string }[] = [
  { value: 'goal_difference',  label: 'Разница шайб' },
  { value: 'goals_scored',     label: 'Забитые шайбы' },
  { value: 'head_to_head',     label: 'Личные встречи' },
  { value: 'regulation_wins',  label: 'Победы в основное время' },
  { value: 'ot_wins',          label: 'Победы в ОТ/буллиты' },
];

export const HockeySettingsForm: React.FC<SportSettingsFormProps> = ({ value, onChange, disabled }) => {
  const s = value as HockeySettings;
  const upd = (patch: Partial<HockeySettings>) => onChange({ ...s, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SettingsSection title="Состав и структура">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Игроков на льду" type="number" size="small" fullWidth
              value={s.playersOnIce ?? 6}
              onChange={(e) => upd({ playersOnIce: parseInt(e.target.value) || 6 })}
              slotProps={{ htmlInput: { min: 1, max: 6 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Периодов" type="number" size="small" fullWidth
              value={s.periods ?? 3}
              onChange={(e) => upd({ periods: parseInt(e.target.value) || 3 })}
              slotProps={{ htmlInput: { min: 1, max: 5 } }}
              disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Длина периода, мин" type="number" size="small" fullWidth
              value={s.periodDurationMin ?? 20}
              onChange={(e) => upd({ periodDurationMin: parseInt(e.target.value) || 20 })}
              slotProps={{ htmlInput: { min: 1 } }}
              disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Очки">
        <SettingsFieldsGrid>
          <SettingsField>
            <TextField label="Победа в ОВ" type="number" size="small" fullWidth
              value={s.pointsWin ?? 2}
              onChange={(e) => upd({ pointsWin: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Победа в ОТ/БУЛ" type="number" size="small" fullWidth
              value={s.pointsWinOT ?? 2}
              onChange={(e) => upd({ pointsWinOT: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Поражение в ОТ/БУЛ" type="number" size="small" fullWidth
              value={s.pointsLossOT ?? 1}
              onChange={(e) => upd({ pointsLossOT: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
          <SettingsField>
            <TextField label="Поражение в ОВ" type="number" size="small" fullWidth
              value={s.pointsLoss ?? 0}
              onChange={(e) => upd({ pointsLoss: parseInt(e.target.value) })}
              slotProps={{ htmlInput: { min: 0 } }} disabled={disabled} />
          </SettingsField>
        </SettingsFieldsGrid>
      </SettingsSection>

      <SettingsSection title="Овертайм">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1, mb: s.overtime ? 2 : 0 }}>
          <FormControlLabel
            control={<Switch checked={s.overtime ?? true} onChange={(e) => upd({ overtime: e.target.checked })} disabled={disabled} />}
            label="Овертайм"
          />
          <FormControlLabel
            control={<Switch checked={s.shootout ?? true} onChange={(e) => upd({ shootout: e.target.checked })} disabled={disabled} />}
            label="Буллиты"
          />
        </Box>
        {s.overtime && (
          <SettingsFieldsGrid>
            <SettingsField>
              <TextField label="Длина ОТ, мин" type="number" size="small" fullWidth
                value={s.overtimeDurationMin ?? 5}
                onChange={(e) => upd({ overtimeDurationMin: parseInt(e.target.value) || 5 })}
                slotProps={{ htmlInput: { min: 1 } }}
                disabled={disabled} />
            </SettingsField>
          </SettingsFieldsGrid>
        )}
      </SettingsSection>

      <SettingsSection title="Тай-брейки">
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={TIEBREAKER_OPTIONS.map((o) => o.value)}
          value={s.tieBreakers ?? []}
          onChange={(_, v) => upd({ tieBreakers: v as HockeyTieBreaker[] })}
          getOptionLabel={(o) => TIEBREAKER_OPTIONS.find((x) => x.value === o)?.label ?? o}
          disabled={disabled}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
              {TIEBREAKER_OPTIONS.find((x) => x.value === option)?.label ?? option}
            </li>
          )}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip
                label={`${idx + 1}. ${TIEBREAKER_OPTIONS.find((x) => x.value === opt)?.label ?? opt}`}
                size="small" {...getTagProps({ index: idx })} key={opt}
              />
            ))
          }
          renderInput={(params) => <TextField {...params} size="small" fullWidth placeholder="Добавить критерий..." />}
        />
      </SettingsSection>

    </Box>
  );
};
